import {
  Box,
  HStack,
  IconButton,
  Stack,
  Text,
  Image,
  Spinner,
  Center,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import {
  Reorder,
  useDragControls,
  useMotionValue,
  DragControls,
} from "framer-motion";
import { chakra } from "@chakra-ui/react";
import { SectionProps } from "../sections/sectionsComponents";
import {
  EventDetailStorySection,
  KYDStoryAsset,
} from "@common/interfaces/KYDEvent";
import ShowIfHasPermission from "../../ShowIfHasPermission";
import { FaPlus } from "react-icons/fa";
import ReactS3Uploader from "react-s3-uploader";
import { useRef, useState } from "react";
import actions from "../../../actions";
import { useParams } from "react-router-dom";
import { useDialog, useDialogWithOptions } from "../../../Common/Dialog";
import { useLoading } from "../../hooks/form";
import DraggableAsset from "./DraggableAsset";

const Item = chakra(Reorder.Item);
const ReorderGroup = chakra(Reorder.Group);

const StorySection = ({
  data,
  permissions,
  isLoading: sectionIsLoading,
}: SectionProps<EventDetailStorySection["data"]>) => {
  const inputUploadRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const { event_id } = useParams();
  const [assets, setAssets] = useState(data.story?.items || []);
  const [loading, updateLoading] = useLoading({
    init: false,
    upload: false,
  });
  const dialog = useDialog();

  const IMAGE_MAX_SIZE = 5 * 1024 * 1024; // 5MB for images
  const VIDEO_MAX_SIZE = 50 * 1024 * 1024; // 50MB for videos

  const openFileUpload = () => {
    if (inputUploadRef?.current) {
      inputUploadRef.current.click();
    }
  };

  const onFileUpload = async (fileKey: string) => {
    try {
      const result = await actions.ADMIN.createEventStory(event_id, fileKey);
      console.log("result", result);

      setAssets(result?.story?.items || []);
    } catch (err) {
      dialog({ text: err.message });
    }
  };

  const handleDeleteAsset = async (asset: KYDStoryAsset) => {
    try {
      const updatedAssets = assets
        .filter((a) => a.key !== asset.key)
        .map((asset, index) => ({
          ...asset,
          position: index,
        }));

      await actions.ADMIN.updateEventStory(event_id, updatedAssets);

      setAssets(updatedAssets);
    } catch (err) {
      dialog({ text: err.message });
    }
  };

  return (
    <Stack position="relative">
      <HStack justify={"space-between"} w="100%">
        <Text fontWeight={"bold"}>Story ({assets.length} / 10)</Text>
        <ShowIfHasPermission permissions={permissions} checkFor="write">
          <Tooltip
            label="You can only have 10 story items at a time."
            isDisabled={assets.length < 10}
          >
            <IconButton
              as="label"
              htmlFor="story-asset-upload"
              isDisabled={
                sectionIsLoading || assets.length >= 10 || isUploading
              }
              variant={"outline"}
              aria-label="Add story assets"
              icon={<FaPlus />}
              cursor="pointer"
              onClick={(e) => {
                if (assets.length >= 10 || isUploading) {
                  e.preventDefault();
                  return;
                }
                openFileUpload();
              }}
            />
          </Tooltip>
        </ShowIfHasPermission>
      </HStack>
      <Box
        position="relative"
        overflowX="auto"
        pb={4}
        minH={isUploading || loading.init ? "200px" : "auto"}
      >
        {isUploading && (
          <Center
            minH="200px"
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            backdropFilter="blur(6px)"
            zIndex={2}
            flexDirection="column"
            borderRadius="md"
          >
            <Spinner size="lg" mb={4} color="white" />
            <Text color="white">{uploadProgress}% uploaded</Text>
          </Center>
        )}

        {loading.init ? (
          <Center>
            <Spinner size="lg" mb={4} />
            <Text>Loading assets...</Text>
          </Center>
        ) : assets.length === 0 ? (
          <Text color="gray.500">No story assets added</Text>
        ) : (
          <ReorderGroup
            axis="x"
            values={assets}
            onReorder={async (newOrder) => {
              // Update positions based on new order
              const updatedAssets = newOrder.map((asset, index) => ({
                ...asset,
                position: index,
              }));

              try {
                await actions.ADMIN.updateEventStory(event_id, updatedAssets);
                setAssets(updatedAssets);
              } catch (err) {
                dialog({ text: err.message });
              }
            }}
          >
            <HStack wrap="nowrap" minW="min-content" spacing={4} pr={4}>
              {assets
                ? assets
                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                    .map((asset, index) => (
                      <DraggableAsset
                        key={asset.key}
                        asset={asset}
                        onDelete={handleDeleteAsset}
                      />
                    ))
                : null}
            </HStack>
          </ReorderGroup>
        )}
      </Box>
      <ReactS3Uploader
        style={{ display: "none" }}
        //@ts-ignore
        inputRef={inputUploadRef}
        getSignedUrl={(file, callback) => {
          console.log("Getting signed URL for file:", file.name, file.type);
          return actions.ADMIN.createUploadUrl(file, callback);
        }}
        accept="image/jpeg,image/png,video/mp4,video/webm"
        onProgress={(p) => {
          console.log("Upload progress:", p);
          setUploadProgress(p);
        }}
        onError={(err) => {
          console.error("Upload error:", {
            fullError: err,
          });
          setIsUploading(false);
          setUploadProgress(0);
          dialog({
            text: `Upload failed: ${err || err || "Please try again."}`,
          });
        }}
        preprocess={(file, next) => {
          const isVideo = file.type.startsWith("video/");
          const maxSize = isVideo ? VIDEO_MAX_SIZE : IMAGE_MAX_SIZE;

          console.log("Preprocessing file:", {
            name: file.name,
            type: file.type,
            size: file.size,
            isVideo,
            maxSize,
            sizeInMB: (file.size / (1024 * 1024)).toFixed(2) + "MB",
          });

          if (file.size > maxSize) {
            dialog({
              text: `File size exceeds ${
                isVideo ? "50MB" : "5MB"
              } limit. Please choose a smaller file.`,
            });
            setIsUploading(false);
            return;
          }
          setIsUploading(true);
          next(file);
        }}
        onFinish={async (result) => {
          console.log("Upload finished:", {
            fileKey: result.fileKey,
            fullResult: result,
          });
          try {
            await onFileUpload(result.fileKey);
          } finally {
            setUploadProgress(0);
            setIsUploading(false);
          }
        }}
        contentDisposition="auto"
        uploadRequestHeaders={(file) => ({
          "Content-Type": file.type,
          "x-amz-acl": "private",
        })}
      />
    </Stack>
  );
};

export default StorySection;
